import type { Condition as ConditionType } from '@/types';
import { isDateInPeriod } from '@/components/calendar/utils';
import Converter from '@/utils/converter';
import UrlQuery from '@/utils/url-query';

export default class Condition {
  public static keyInQueryParamsOrCookiesEqual(key: string, expectedValue: string): boolean;
  public static keyInQueryParamsOrCookiesEqual(key: string, expectedValue: number): boolean;
  public static keyInQueryParamsOrCookiesEqual(key: string, expectedValue: boolean): boolean;

  public static keyInQueryParamsOrCookiesEqual(key: string, expectedValue: boolean | number | string) {
    switch (typeof expectedValue) {
      case 'string':
        return UrlQuery.get(key).string() === expectedValue;
      case 'number':
        return UrlQuery.get(key).number() === expectedValue;
      case 'boolean':
        return UrlQuery.get(key).boolean() === expectedValue;
      default:
        return false;
    }
  }

  public static getFirst<V, F>(list: ConditionType<V>[], fb: F): V | F;
  public static getFirst<V>(list: ConditionType<V>[], fb?: undefined): V | undefined;
  public static getFirst<V>(list: ConditionType<V>[], fb: V): V;
  public static getFirst<V>(list: ConditionType<V>[], fb: V) {
    return list.find((item) => item.condition())?.value || fb;
  }

  public static date(baseDate: string) {
    const base = Converter.DateSting.ToNumber(baseDate);
    const valid = Number.isFinite(base) && base > 0;
    return {
      more(compareToDate: string): boolean {
        return valid && base > Converter.DateSting.ToNumber(compareToDate);
      },
      moreOrEqual(compareToDate: string): boolean {
        return valid && base >= Converter.DateSting.ToNumber(compareToDate);
      },
      less(compareToDate: string): boolean {
        return valid && base < Converter.DateSting.ToNumber(compareToDate);
      },
      lessOrEqual(compareToDate: string): boolean {
        return valid && base <= Converter.DateSting.ToNumber(compareToDate);
      },
      equal(compareToDate: string): boolean {
        return valid && base === Converter.DateSting.ToNumber(compareToDate);
      },
    };
  }

  public static messages(...args: [boolean, string][]): string[] {
    const messages: string[] = [];
    args.forEach(([condition, message]) => {
      if (condition) messages.push(message);
    });
    return messages;
  }

  public static isEmpty(v: unknown): boolean {
    return v === '' || v === undefined || v === null;
  }

  public static isNull(v: unknown): boolean {
    return v === null;
  }

  public static isNotNull(v: unknown): boolean {
    return v !== null;
  }

  public static isDefined(v: unknown): boolean {
    return v !== undefined && v !== null;
  }

  public static isNotDefined(v: unknown): boolean {
    return v === undefined || v === null;
  }

  public static isNotUndefined(v: unknown): boolean {
    return v !== undefined;
  }

  public static isUndefined(v: unknown): boolean {
    return v === undefined;
  }

  public static isInPeriod(v: string, period: [string, string]): boolean {
    return isDateInPeriod(Converter.DateSting.ToDate(v), period);
  }

  public static isNotInPeriod(v: string, period: [string, string]): boolean {
    return !isDateInPeriod(Converter.DateSting.ToDate(v), period);
  }

  public static isDigitOnly(v: string): boolean {
    return !new RegExp(/^-?\d+([.,]\d+)?$/).test(v);
  }

  public static isLongerLength(v: string, maxLength: number): boolean {
    return v.length > maxLength;
  }

  public static isShorterLength(v: string, maxLength: number): boolean {
    return v.length < maxLength;
  }

  public static isExactLength(v: string, maxLength: number): boolean {
    return v.length === maxLength;
  }
}
